import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Home,
  Building2,
  Sparkles,
  Calculator,
  Info,
  MessageSquareText,
  Paintbrush,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent } from "./components/ui/card";
import { calculate } from "./calculator";
import { LABELS } from "./pricing";

const fmtEUR = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const windowCount = (fenster = {}) => ["standard", "klein", "zweifluegelig", "bodentief", "dachfenster"]
  .reduce((sum, key) => sum + (Number(fenster[key]) || 0), 0);
const includesCleaning = (serviceType) => ["cleaning", "combined"].includes(serviceType);
const includesPainting = (serviceType) => ["painting", "combined"].includes(serviceType);
const isStudio = (objectType) => ["fitnessstudio", "pilatesstudio", "yogastudio", "studio"].includes(objectType);

const StepDot = ({ n, active, done, label }) => (
  <div className="flex flex-col items-center flex-1 min-w-0" data-testid={`step-dot-${n}`}>
    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition-colors ${
      done ? "bg-[#10B981] text-white" : active ? "bg-[#0066CC] text-white" : "bg-slate-200 text-slate-500"
    }`}>
      {done ? <Check className="w-4 h-4" /> : n}
    </div>
    <span className={`mt-2 text-[10px] sm:text-xs text-center leading-tight ${active ? "text-[#0F172A] font-semibold" : "text-[#64748B]"}`}>{label}</span>
  </div>
);

const StepBar = ({ step }) => {
  const steps = [
    "Leistung",
    "Kundentyp",
    "Objektart",
    "Objektdaten",
    "Häufigkeit",
    "Details",
    "Ergebnis",
  ];
  return (
    <div className="flex items-start gap-1 mb-8 overflow-x-auto pb-2">
      {steps.map((label, i) => (
        <React.Fragment key={label}>
          <StepDot n={i + 1} active={step === i + 1} done={step > i + 1} label={label} />
          {i < steps.length - 1 && <div className={`hidden md:block flex-1 h-0.5 mt-4 ${step > i + 1 ? "bg-[#10B981]" : "bg-slate-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};

const Chip = ({ selected, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left border-2 rounded-2xl p-5 transition-all hover:border-[#0066CC] hover:shadow-md ${
      selected ? "border-[#0066CC] bg-[#EFF6FF] shadow-md" : "border-slate-200 bg-white"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, children, hint, error, testid }) => (
  <div className="space-y-1" data-testid={testid ? `field-${testid}` : undefined}>
    <label className="text-sm font-semibold text-[#0F172A]">{label}</label>
    {children}
    {hint && <p className="text-xs text-[#64748B]">{hint}</p>}
    {error && <p className="text-xs font-medium text-red-600">{error}</p>}
  </div>
);

const NumberInput = ({ value, onChange, min = 0, testid, ...rest }) => (
  <Input
    type="number"
    min={min}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
    data-testid={testid}
    className="h-11"
    {...rest}
  />
);

const SelectInput = ({ value, onChange, children, testid }) => (
  <select
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className="w-full h-11 rounded-md border border-input bg-background px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0066CC]/30"
  >
    {children}
  </select>
);

const YesNo = ({ label, value, onChange, testid }) => (
  <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-200 bg-white">
    <span className="text-sm font-medium text-[#0F172A]">{label}</span>
    <div className="flex gap-2 flex-shrink-0">
      {[["Ja", true], ["Nein", false]].map(([txt, v]) => (
        <button
          key={txt}
          type="button"
          onClick={() => onChange(v)}
          data-testid={`${testid}-${v ? "yes" : "no"}`}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            value === v ? "bg-[#0066CC] text-white" : "bg-slate-100 text-[#334155] hover:bg-slate-200"
          }`}
        >
          {txt}
        </button>
      ))}
    </div>
  </div>
);

const emptyBalcony = () => ({ size: "", soilLevel: "", weeds: "", specialCase: false });

const INITIAL_STATE = {
  serviceType: "",
  individualDescription: "",
  customerType: "",
  objectType: "",
  customObjectDescription: "",
  housingCleaningType: "",
  sqm: "",
  zimmer: "",
  baeder: 1,
  gaesteWC: 0,
  kuechen: 1,
  etagen: 1,
  balconyPresent: null,
  balconyCount: 1,
  balconies: [emptyBalcony()],
  toiletten: 0,
  arbeitsplaetze: "",
  duschen: 0,
  umkleiden: 0,
  grosseSpiegel: false,
  starkFrequentiert: false,
  studioNotes: "",
  officeOneTimeType: "",
  stockwerke: 1,
  eingaenge: 1,
  aufzug: false,
  kellerflur: false,
  hauseingangAussen: false,
  fenster: {
    standard: 0,
    klein: 0,
    zweifluegelig: 0,
    bodentief: 0,
    dachfenster: 0,
    schwerErreichbar: false,
    soilLevel: "",
  },
  frequency: "",
  customVisitsPerMonth: "",
  customFrequencyText: "",
  soilLevel: "normal",
  backofen: false,
  kuehlschrank: false,
  kuechenschraenke: false,
  tierhaare: false,
  raucher: false,
  paintingSqm: "",
  paintingRooms: "",
  paintingScope: "",
  paintingCurrentColor: "",
  paintingDesiredColor: "",
  paintingCondition: "",
  paintingRoomsEmpty: null,
  paintingFurniture: false,
  paintingSmallRepairs: false,
  paintingHighCeiling: false,
  paintingNotes: "",
};

export default function AngebotPage({ NavigationComp, FooterComp }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);
  const [step, setStep] = useState(1);
  const [state, setState] = useState(INITIAL_STATE);
  const [validationMessage, setValidationMessage] = useState("");

  useEffect(() => {
    document.title = "Angebot anfordern – Kalkulationsrechner | Frankfurt Reinigung 24";
    window.scrollTo(0, 0);
  }, []);

  const update = (patch) => setState((current) => ({ ...current, ...patch }));
  const updateFenster = (patch) => setState((current) => ({ ...current, fenster: { ...current.fenster, ...patch } }));
  const isBusiness = ["unternehmen", "hausverwaltung", "praxis"].includes(state.customerType);

  const setBalconyCount = (countValue) => {
    const count = Math.max(1, Number(countValue) || 1);
    setState((current) => {
      const next = Array.from({ length: count }, (_, index) => current.balconies[index] || emptyBalcony());
      return { ...current, balconyCount: count, balconies: next };
    });
  };

  const updateBalcony = (index, patch) => {
    setState((current) => ({
      ...current,
      balconies: current.balconies.map((item, i) => i === index ? { ...item, ...patch } : item),
    }));
  };

  const result = useMemo(() => step === 7 ? calculate(state) : null, [step, state]);

  const availableObjectTypes = useMemo(() => {
    const entries = Object.entries(LABELS.objectTypes);
    if (includesPainting(state.serviceType)) return entries.filter(([key]) => key !== "fenster");
    return entries;
  }, [state.serviceType]);

  const validateStep = () => {
    if (step === 1) {
      if (!state.serviceType) return "Bitte wählen Sie eine Leistung aus.";
      if (state.serviceType === "individual" && !state.individualDescription.trim()) return "Bitte beschreiben Sie die gewünschten Arbeiten.";
    }
    if (step === 2 && !state.customerType) return "Bitte wählen Sie einen Kundentyp aus.";
    if (step === 3) {
      if (!state.objectType) return "Bitte wählen Sie eine Objektart aus.";
      if (state.objectType === "sonstiges" && !state.customObjectDescription.trim()) return "Bitte beschreiben Sie das sonstige Objekt.";
    }
    if (step === 4) {
      if (includesCleaning(state.serviceType)) {
        if (["wohnung", "haus", "buero", "praxis"].includes(state.objectType) && !(Number(state.sqm) > 0)) return "Bitte geben Sie die Quadratmeterzahl ein.";
        if (isStudio(state.objectType) && !(Number(state.sqm) > 0)) return "Bitte geben Sie die Studiofläche ein.";
        if (state.objectType === "treppenhaus" && !(Number(state.stockwerke) > 0)) return "Bitte geben Sie die Anzahl der Stockwerke ein.";
        if (state.objectType === "fenster" && windowCount(state.fenster) <= 0) return "Bitte geben Sie mindestens ein Fenster an.";
        if (["wohnung", "haus"].includes(state.objectType)) {
          if (!state.housingCleaningType) return "Bitte wählen Sie aus, ob es sich um eine bewohnte Standardreinigung oder eine Endreinigung nach Auszug handelt.";
          if (state.balconyPresent === null) return "Bitte geben Sie an, ob ein Balkon oder eine Terrasse vorhanden ist.";
          if (state.balconyPresent) {
            if (!(Number(state.balconyCount) > 0)) return "Bitte geben Sie die Anzahl der Balkone an.";
            const incompleteIndex = state.balconies.findIndex((item) => !item.size || !item.soilLevel || !item.weeds);
            if (incompleteIndex >= 0) return `Bitte vervollständigen Sie Größe, Verschmutzung und Unkraut für Balkon ${incompleteIndex + 1}.`;
          }
        }
      }
      if (includesPainting(state.serviceType)) {
        if (state.objectType === "treppenhaus") {
          if (!(Number(state.stockwerke) > 0)) return "Bitte geben Sie die Anzahl der Stockwerke ein.";
        } else {
          if (!(Number(state.paintingSqm || state.sqm) > 0)) return "Bitte geben Sie die zu kalkulierende Bodenfläche für die Malerarbeiten ein.";
          if (!state.paintingScope) return "Bitte wählen Sie aus, ob nur Wände oder auch Decken gestrichen werden sollen.";
          if (!state.paintingCurrentColor) return "Bitte wählen Sie die aktuelle Wandfarbe beziehungsweise Deckkraft aus.";
          if (!state.paintingCondition) return "Bitte wählen Sie den Wandzustand aus.";
          if (!state.paintingDesiredColor.trim()) return "Bitte geben Sie die gewünschte neue Farbe an.";
        }
      }
    }
    if (step === 5) {
      if (["wohnung", "haus"].includes(state.objectType) && state.housingCleaningType === "end" && state.frequency !== "einmalig") {
        return "Eine Endreinigung nach Auszug wird als einmaliger Auftrag kalkuliert.";
      }
      if (!state.frequency) return "Bitte wählen Sie die Häufigkeit aus.";
      if (state.frequency === "individuell") {
        if (!(Number(state.customVisitsPerMonth) > 0)) return "Bitte geben Sie die Anzahl der Einsätze pro Monat ein.";
        if (!state.customFrequencyText.trim()) return "Bitte beschreiben Sie den gewünschten Turnus.";
      }
      if (["buero", "praxis"].includes(state.objectType) && state.frequency === "einmalig" && includesCleaning(state.serviceType) && !state.officeOneTimeType) {
        return "Bitte wählen Sie Standardreinigung oder intensive Grundreinigung aus.";
      }
    }
    if (step === 6 && includesCleaning(state.serviceType)) {
      if (windowCount(state.fenster) > 0 && !state.fenster.soilLevel) return "Bitte wählen Sie den Verschmutzungsgrad der Fenster aus.";
    }
    return "";
  };

  const nextStep = () => {
    const message = validateStep();
    if (message) {
      setValidationMessage(message);
      toast.error(message);
      return;
    }
    setValidationMessage("");
    setStep((current) => Math.min(7, current + 1));
  };

  const selectService = (serviceType) => {
    update({
      serviceType,
      frequency: serviceType === "cleaning" ? state.frequency : "einmalig",
      objectType: serviceType === "painting" && state.objectType === "fenster" ? "" : state.objectType,
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {NavigationComp && <NavigationComp isSubpage={true} />}
      <div className="pt-20 bg-[#0F172A] py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="back-home-btn">
            <ArrowLeft className="w-5 h-5" />Zurück zur Startseite
          </button>
          <span className="text-[#60A5FA] font-semibold text-sm uppercase tracking-widest">Angebot anfordern</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mt-3 mb-3">Ihr persönliches Angebot</h1>
          <p className="text-lg text-slate-300 max-w-2xl">Senden Sie uns direkt eine Anfrage oder berechnen Sie zuerst einen unverbindlichen Richtpreis.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {!mode && <OfferModeSelection onSelect={setMode} />}

        {mode === "direct" && (
          <div>
            <Button variant="outline" onClick={() => setMode(null)} className="rounded-full mb-6"><ArrowLeft className="w-4 h-4 mr-2" /> Auswahl ändern</Button>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-[#0F172A]">Direkt Angebot anfragen</h2>
              <p className="text-[#64748B] mt-2">Beschreiben Sie Ihr Objekt und die gewünschten Leistungen. Wir erstellen Ihnen ein individuelles Angebot.</p>
            </div>
            <QuoteRequestForm direct />
          </div>
        )}

        {mode === "calculator" && (
          <>
            <Button variant="outline" onClick={() => { setMode(null); setStep(1); }} className="rounded-full mb-6"><ArrowLeft className="w-4 h-4 mr-2" /> Auswahl ändern</Button>
            <StepBar step={step} />
            <Card className="border-0 shadow-xl rounded-2xl">
              <CardContent className="p-6 md:p-10">
                {step === 1 && (
                  <div data-testid="step-1">
                    <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Gewünschte Leistung</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {Object.entries(LABELS.serviceTypes).map(([key, label]) => (
                        <Chip key={key} selected={state.serviceType === key} onClick={() => selectService(key)} testid={`service-${key}`}>
                          <div className="flex items-center gap-3">
                            {key === "painting" ? <Paintbrush className="w-6 h-6 text-[#0066CC]" /> : key === "individual" ? <MessageSquareText className="w-6 h-6 text-[#0066CC]" /> : <Sparkles className="w-6 h-6 text-[#0066CC]" />}
                            <span className="font-semibold text-[#0F172A]">{label}</span>
                          </div>
                        </Chip>
                      ))}
                    </div>
                    {state.serviceType === "individual" && (
                      <div className="mt-5">
                        <Field label="Bitte beschreiben Sie die gewünschten Arbeiten *">
                          <Textarea value={state.individualDescription} onChange={(e) => update({ individualDescription: e.target.value })} rows={5} />
                        </Field>
                      </div>
                    )}
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="step-2">
                    <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Kundentyp wählen</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {Object.entries(LABELS.customerTypes).map(([key, label]) => (
                        <Chip key={key} selected={state.customerType === key} onClick={() => update({ customerType: key })} testid={`ct-${key}`}>
                          <div className="flex items-center gap-3">
                            {key === "privat" ? <Home className="w-6 h-6 text-[#0066CC]" /> : <Building2 className="w-6 h-6 text-[#0066CC]" />}
                            <span className="font-semibold text-[#0F172A]">{label}</span>
                          </div>
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="step-3">
                    <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Objektart wählen</h2>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {availableObjectTypes.map(([key, label]) => (
                        <Chip key={key} selected={state.objectType === key} onClick={() => update({ objectType: key })} testid={`ot-${key}`}>
                          <span className="font-semibold text-[#0F172A]">{label}</span>
                        </Chip>
                      ))}
                    </div>
                    {state.objectType === "sonstiges" && (
                      <div className="mt-5">
                        <Field label="Welche Art von Objekt soll gereinigt oder bearbeitet werden? *">
                          <Textarea value={state.customObjectDescription} onChange={(e) => update({ customObjectDescription: e.target.value })} rows={4} />
                        </Field>
                      </div>
                    )}
                  </div>
                )}

                {step === 4 && (
                  <ObjectDataStep
                    state={state}
                    update={update}
                    updateFenster={updateFenster}
                    setBalconyCount={setBalconyCount}
                    updateBalcony={updateBalcony}
                  />
                )}

                {step === 5 && (
                  <FrequencyStep state={state} update={update} />
                )}

                {step === 6 && (
                  <DetailsStep state={state} update={update} updateFenster={updateFenster} />
                )}

                {step === 7 && result && (
                  <ResultSection result={result} state={state} isBusiness={isBusiness} onEdit={() => setStep(4)} />
                )}
              </CardContent>
            </Card>

            {step < 7 && (
              <>
                {validationMessage && <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{validationMessage}</div>}
                <div className="flex justify-between items-center mt-6">
                  <Button variant="outline" onClick={() => { setValidationMessage(""); setStep((current) => Math.max(1, current - 1)); }} disabled={step === 1} data-testid="wizard-back" className="rounded-full px-6">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
                  </Button>
                  <Button onClick={nextStep} data-testid="wizard-next" className="bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full px-8">
                    {step === 6 ? "Preis berechnen" : "Weiter"} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
      {FooterComp && <FooterComp />}
    </div>
  );
}

function OfferModeSelection({ onSelect }) {
  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-[#0F172A]">Wie möchten Sie Ihr Angebot anfordern?</h2>
        <p className="text-[#64748B] mt-3">Wählen Sie den schnellsten Weg für Ihre Anfrage.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <button type="button" onClick={() => onSelect("direct")} className="text-left bg-white border-2 border-slate-200 hover:border-[#0066CC] rounded-2xl p-7 shadow-sm hover:shadow-lg transition-all">
          <MessageSquareText className="w-10 h-10 text-[#0066CC] mb-4" />
          <h3 className="text-xl font-bold text-[#0F172A]">Direkt Angebot anfragen</h3>
          <p className="text-[#64748B] mt-2 mb-5">Beschreiben Sie uns Ihr Objekt und die gewünschten Leistungen. Wir erstellen Ihnen ein individuelles Angebot.</p>
          <span className="inline-flex items-center font-semibold text-[#0066CC]">Anfrage schreiben <ArrowRight className="w-4 h-4 ml-2" /></span>
        </button>
        <button type="button" onClick={() => onSelect("calculator")} className="text-left bg-[#EFF6FF] border-2 border-[#0066CC] rounded-2xl p-7 shadow-sm hover:shadow-lg transition-all">
          <Calculator className="w-10 h-10 text-[#0066CC] mb-4" />
          <h3 className="text-xl font-bold text-[#0F172A]">Preis selbst kalkulieren</h3>
          <p className="text-[#64748B] mt-2 mb-5">Berechnen Sie in wenigen Schritten einen unverbindlichen Richtpreis für Reinigung oder Malerarbeiten.</p>
          <span className="inline-flex items-center font-semibold text-[#0066CC]">Preis kalkulieren <ArrowRight className="w-4 h-4 ml-2" /></span>
        </button>
      </div>
    </div>
  );
}

function ObjectDataStep({ state, update, updateFenster, setBalconyCount, updateBalcony }) {
  return (
    <div data-testid="step-4" className="space-y-8">
      <h2 className="text-2xl font-bold text-[#0F172A]">Objektdaten</h2>

      {includesCleaning(state.serviceType) && ["wohnung", "haus"].includes(state.objectType) && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-bold text-[#0F172A] mb-3">Welche Wohnungsreinigung benötigen Sie? *</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.entries(LABELS.housingCleaningTypes).map(([key, label]) => (
                <Chip
                  key={key}
                  selected={state.housingCleaningType === key}
                  onClick={() => update({
                    housingCleaningType: key,
                    frequency: key === "end" ? "einmalig" : state.frequency,
                  })}
                  testid={`housing-cleaning-${key}`}
                >
                  <span className="font-semibold text-[#0F172A]">{label}</span>
                  <p className="text-sm text-[#64748B] mt-1">
                    {key === "occupied"
                      ? "Für bewohnte Wohnungen: frei zugängliche Oberflächen, Staubsaugen, Wischen, Küche außen, 1 Bad/WC und bis zu 3 leicht verschmutzte Standardfenster inklusive. Backofen, Kühlschrank und Schränke innen sind nur als Zusatzleistung enthalten."
                      : "Für die gründliche Reinigung nach dem Auszug. Fenster und Sonderleistungen werden passend zum Aufwand ergänzt."}
                  </p>
                </Chip>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Quadratmeter (m²) *"><NumberInput value={state.sqm} onChange={(v) => update({ sqm: v })} min={1} testid="input-sqm" /></Field>
          <Field label="Anzahl Zimmer"><NumberInput value={state.zimmer} onChange={(v) => update({ zimmer: v })} min={1} testid="input-zimmer" /></Field>
          <Field label="Anzahl Badezimmer"><NumberInput value={state.baeder} onChange={(v) => update({ baeder: v })} min={1} testid="input-baeder" /></Field>
          <Field label="Anzahl Gäste-WCs"><NumberInput value={state.gaesteWC} onChange={(v) => update({ gaesteWC: v })} min={0} testid="input-gaestewc" /></Field>
          <Field label="Anzahl Küchen"><NumberInput value={state.kuechen} onChange={(v) => update({ kuechen: v })} min={0} testid="input-kuechen" /></Field>
          {state.objectType === "haus" && <Field label="Anzahl Etagen"><NumberInput value={state.etagen} onChange={(v) => update({ etagen: v })} min={1} testid="input-etagen" /></Field>}
          <div className="sm:col-span-2">
            <YesNo label="Balkon oder Terrasse vorhanden? *" value={state.balconyPresent} onChange={(v) => update({ balconyPresent: v, balconies: v ? (state.balconies.length ? state.balconies : [emptyBalcony()]) : [], balconyCount: v ? Math.max(1, state.balconyCount || 1) : 0 })} testid="yn-balkon" />
          </div>
          {state.balconyPresent && (
            <div className="sm:col-span-2 space-y-4">
              <Field label="Anzahl Balkone / Terrassen *">
                <NumberInput value={state.balconyCount} onChange={setBalconyCount} min={1} testid="input-balcony-count" />
              </Field>
              {state.balconies.map((balcony, index) => (
                <div key={index} className="border border-slate-200 rounded-2xl p-5 bg-slate-50">
                  <h3 className="font-bold text-[#0F172A] mb-4">Balkon / Terrasse {index + 1}</h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="Größe *">
                      <SelectInput value={balcony.size} onChange={(value) => updateBalcony(index, { size: value })} testid={`balcony-size-${index}`}>
                        <option value="">Bitte wählen</option>
                        {Object.entries(LABELS.balconySizes).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </SelectInput>
                    </Field>
                    <Field label="Verschmutzung *">
                      <SelectInput value={balcony.soilLevel} onChange={(value) => updateBalcony(index, { soilLevel: value })} testid={`balcony-soil-${index}`}>
                        <option value="">Bitte wählen</option>
                        {Object.entries(LABELS.balconySoilLevels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </SelectInput>
                    </Field>
                    <Field label="Unkraut *">
                      <SelectInput value={balcony.weeds} onChange={(value) => updateBalcony(index, { weeds: value })} testid={`balcony-weeds-${index}`}>
                        <option value="">Bitte wählen</option>
                        {Object.entries(LABELS.weeds).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </SelectInput>
                    </Field>
                  </div>
                  <div className="mt-4">
                    <YesNo label="Taubenkot, starke Vermoosung, große Müllmenge oder Sonderentsorgung?" value={balcony.specialCase} onChange={(v) => updateBalcony(index, { specialCase: v })} testid={`balcony-special-${index}`} />
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      )}

      {includesCleaning(state.serviceType) && ["buero", "praxis"].includes(state.objectType) && (
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Quadratmeter (m²) *"><NumberInput value={state.sqm} onChange={(v) => update({ sqm: v })} min={1} testid="input-sqm" /></Field>
          <Field label="Anzahl Toiletten"><NumberInput value={state.toiletten} onChange={(v) => update({ toiletten: v })} min={0} testid="input-toiletten" /></Field>
          <Field label="Küchen / Teeküchen"><NumberInput value={state.kuechen} onChange={(v) => update({ kuechen: v })} min={0} testid="input-kuechen" /></Field>
          <Field label="Anzahl Arbeitsplätze"><NumberInput value={state.arbeitsplaetze} onChange={(v) => update({ arbeitsplaetze: v })} min={0} testid="input-arbeitsplaetze" /></Field>
        </div>
      )}

      {includesCleaning(state.serviceType) && isStudio(state.objectType) && (
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Quadratmeter (m²) *"><NumberInput value={state.sqm} onChange={(v) => update({ sqm: v })} min={1} /></Field>
          <Field label="Anzahl Toiletten"><NumberInput value={state.toiletten} onChange={(v) => update({ toiletten: v })} min={0} /></Field>
          <Field label="Anzahl Duschen"><NumberInput value={state.duschen} onChange={(v) => update({ duschen: v })} min={0} /></Field>
          <Field label="Anzahl Umkleidebereiche"><NumberInput value={state.umkleiden} onChange={(v) => update({ umkleiden: v })} min={0} /></Field>
          <Field label="Küchen / Teeküchen"><NumberInput value={state.kuechen} onChange={(v) => update({ kuechen: v })} min={0} /></Field>
          <div className="sm:col-span-2 space-y-3">
            <YesNo label="Große Spiegel- oder Glasflächen?" value={state.grosseSpiegel} onChange={(v) => update({ grosseSpiegel: v })} testid="yn-mirrors" />
            <YesNo label="Stark frequentiert?" value={state.starkFrequentiert} onChange={(v) => update({ starkFrequentiert: v })} testid="yn-traffic" />
            <Field label="Besonderheiten (optional)"><Textarea value={state.studioNotes} onChange={(e) => update({ studioNotes: e.target.value })} rows={3} /></Field>
          </div>
        </div>
      )}

      {includesCleaning(state.serviceType) && state.objectType === "treppenhaus" && (
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Anzahl Stockwerke *"><NumberInput value={state.stockwerke} onChange={(v) => update({ stockwerke: v })} min={1} testid="input-stockwerke" /></Field>
          <Field label="Anzahl Eingänge"><NumberInput value={state.eingaenge} onChange={(v) => update({ eingaenge: v })} min={1} testid="input-eingaenge" /></Field>
          <div className="sm:col-span-2 space-y-3">
            <YesNo label="Aufzug vorhanden?" value={state.aufzug} onChange={(v) => update({ aufzug: v })} testid="yn-aufzug" />
            <YesNo label="Kellerflur reinigen?" value={state.kellerflur} onChange={(v) => update({ kellerflur: v })} testid="yn-kellerflur" />
            <YesNo label="Hauseingang außen reinigen?" value={state.hauseingangAussen} onChange={(v) => update({ hauseingangAussen: v })} testid="yn-hauseingang" />
          </div>
        </div>
      )}

      {includesCleaning(state.serviceType) && state.objectType === "fenster" && <WindowFields state={state} updateFenster={updateFenster} includeSoil={false} />}

      {includesPainting(state.serviceType) && (
        <div className="border-t border-slate-200 pt-8">
          <h3 className="text-xl font-bold text-[#0F172A] mb-5">Angaben zu den Malerarbeiten</h3>
          <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-4 mb-5 text-sm text-[#0F172A]">
            Standard-Malerarbeiten werden mit 20,00 € je m² Bodenfläche kalkuliert. Enthalten sind weiße Wände, Material, Abdecken und kleinere übliche Vorarbeiten. Dunkle oder stark vergilbte Wände sowie aufwendige Untergrundarbeiten werden mit Zuschlägen beziehungsweise nach Prüfung angeboten.
          </div>
          {state.objectType === "treppenhaus" ? (
            <div className="space-y-4">
              <Field label="Anzahl Stockwerke *"><NumberInput value={state.stockwerke} onChange={(v) => update({ stockwerke: v })} min={1} /></Field>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-[#0F172A]">Malerarbeiten im Treppenhaus werden wegen Höhe, Zugänglichkeit und möglicher Gerüst-/Leiterarbeiten nach Fotos oder Besichtigung angeboten.</div>
              <Field label="Beschreibung der Flächen"><Textarea value={state.paintingNotes} onChange={(e) => update({ paintingNotes: e.target.value })} rows={4} /></Field>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Bodenfläche zur Kalkulation (m²) *"><NumberInput value={state.paintingSqm || state.sqm} onChange={(v) => update({ paintingSqm: v })} min={1} /></Field>
              <Field label="Anzahl Räume"><NumberInput value={state.paintingRooms} onChange={(v) => update({ paintingRooms: v })} min={1} /></Field>
              <Field label="Leistungsumfang *">
                <SelectInput value={state.paintingScope} onChange={(value) => update({ paintingScope: value })}>
                  <option value="">Bitte wählen</option>
                  {Object.entries(LABELS.paintingScopes).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </SelectInput>
              </Field>
              <Field label="Aktuelle Wandfarbe / Deckkraft *">
                <SelectInput value={state.paintingCurrentColor} onChange={(value) => update({ paintingCurrentColor: value })}>
                  <option value="">Bitte wählen</option>
                  {Object.entries(LABELS.paintingColors).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </SelectInput>
              </Field>
              <Field label="Wandzustand *">
                <SelectInput value={state.paintingCondition} onChange={(value) => update({ paintingCondition: value })}>
                  <option value="">Bitte wählen</option>
                  {Object.entries(LABELS.paintingConditions).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </SelectInput>
              </Field>
              <Field label="Gewünschte neue Farbe *"><Input value={state.paintingDesiredColor} onChange={(e) => update({ paintingDesiredColor: e.target.value })} placeholder="z. B. Weiß, RAL-Farbe, Beige" /></Field>
              <div className="sm:col-span-2 space-y-3">
                <YesNo label="Räume leer?" value={state.paintingRoomsEmpty} onChange={(v) => update({ paintingRoomsEmpty: v, paintingFurniture: !v })} testid="yn-empty-rooms" />
                <YesNo label="Viele Möbel oder aufwendiges Abdecken?" value={state.paintingFurniture} onChange={(v) => update({ paintingFurniture: v })} testid="yn-furniture" />
                <YesNo label="Kleine Bohrlöcher oder leichte Risse?" value={state.paintingSmallRepairs} onChange={(v) => update({ paintingSmallRepairs: v, paintingCondition: v && state.paintingCondition === "normal" ? "klein" : state.paintingCondition })} testid="yn-small-repairs" />
                <YesNo label="Raumhöhe über 2,70 Meter?" value={state.paintingHighCeiling} onChange={(v) => update({ paintingHighCeiling: v })} testid="yn-high-ceiling" />
              </div>
              <div className="sm:col-span-2"><Field label="Zusätzliche Hinweise"><Textarea value={state.paintingNotes} onChange={(e) => update({ paintingNotes: e.target.value })} rows={4} /></Field></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FrequencyStep({ state, update }) {
  const isResidentialEndClean = includesCleaning(state.serviceType)
    && ["wohnung", "haus"].includes(state.objectType)
    && state.housingCleaningType === "end";

  if (isResidentialEndClean && state.serviceType === "cleaning") {
    return (
      <div data-testid="step-5" className="space-y-6">
        <h2 className="text-2xl font-bold text-[#0F172A]">Häufigkeit</h2>
        <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-2xl p-5">
          <p className="font-semibold text-[#0F172A]">Einmaliger Auftrag</p>
          <p className="text-sm text-[#64748B] mt-1">Eine Endreinigung nach Auszug wird als einmalige Reinigung kalkuliert.</p>
        </div>
      </div>
    );
  }

  if (includesPainting(state.serviceType) || state.serviceType === "individual") {
    const combinedOffice = state.serviceType === "combined" && ["buero", "praxis"].includes(state.objectType);
    return (
      <div data-testid="step-5" className="space-y-6">
        <h2 className="text-2xl font-bold text-[#0F172A]">Häufigkeit</h2>
        <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-2xl p-5">
          <p className="font-semibold text-[#0F172A]">Einmaliger Auftrag</p>
          <p className="text-sm text-[#64748B] mt-1">
            {state.serviceType === "individual"
              ? "Für die individuelle Anfrage wird zunächst ein einmaliger Auftrag angelegt. Den gewünschten Ablauf können Sie im Formular beschreiben."
              : "Malerarbeiten werden in dieser Kalkulation als einmalige Leistung berechnet. Bei einer Kombination gilt der Richtpreis ebenfalls für den einmaligen Gesamtauftrag."}
          </p>
        </div>
        {combinedOffice && (
          <div>
            <h3 className="text-lg font-bold text-[#0F172A] mb-3">Art der einmaligen Büroreinigung *</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.entries(LABELS.officeOneTimeTypes).map(([key, label]) => (
                <Chip key={key} selected={state.officeOneTimeType === key} onClick={() => update({ officeOneTimeType: key })} testid={`office-onetime-${key}`}>
                  <span className="font-semibold text-[#0F172A]">{label}</span>
                  <p className="text-sm text-[#64748B] mt-1">{key === "standard" ? "Normale einmalige Büroreinigung" : "Intensive Detail- und Grundreinigung"}</p>
                </Chip>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="step-5" className="space-y-6">
      <h2 className="text-2xl font-bold text-[#0F172A]">Häufigkeit</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {Object.entries(LABELS.frequencies).map(([key, label]) => (
          <Chip key={key} selected={state.frequency === key} onClick={() => update({ frequency: key })} testid={`freq-${key}`}>
            <span className="font-semibold text-[#0F172A]">{label}</span>
          </Chip>
        ))}
      </div>
      {state.frequency === "individuell" && (
        <div className="grid sm:grid-cols-2 gap-4 border border-slate-200 rounded-2xl p-5 bg-slate-50">
          <Field label="Wie viele Einsätze pro Monat? *"><NumberInput value={state.customVisitsPerMonth} onChange={(v) => update({ customVisitsPerMonth: v })} min={1} /></Field>
          <Field label="Beschreiben Sie Ihren gewünschten Turnus *"><Textarea value={state.customFrequencyText} onChange={(e) => update({ customFrequencyText: e.target.value })} rows={3} placeholder="z. B. Montag, Mittwoch und Freitag vor 8 Uhr" /></Field>
        </div>
      )}
      {["buero", "praxis"].includes(state.objectType) && state.frequency === "einmalig" && (
        <div>
          <h3 className="text-lg font-bold text-[#0F172A] mb-3">Art der einmaligen Reinigung *</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {Object.entries(LABELS.officeOneTimeTypes).map(([key, label]) => (
              <Chip key={key} selected={state.officeOneTimeType === key} onClick={() => update({ officeOneTimeType: key })} testid={`office-onetime-${key}`}>
                <span className="font-semibold text-[#0F172A]">{label}</span>
                <p className="text-sm text-[#64748B] mt-1">{key === "standard" ? "Normale einmalige Büroreinigung" : "Intensive Detail- und Grundreinigung"}</p>
              </Chip>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WindowFields({ state, updateFenster, includeSoil = true }) {
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Standardfenster" hint="Innen und außen inklusive Rahmen"><NumberInput value={state.fenster.standard} onChange={(v) => updateFenster({ standard: v })} testid="input-f-standard" /></Field>
        <Field label="Kleine WC-/Kellerfenster"><NumberInput value={state.fenster.klein} onChange={(v) => updateFenster({ klein: v })} testid="input-f-klein" /></Field>
        <Field label="Zweiflügelige Fenster"><NumberInput value={state.fenster.zweifluegelig} onChange={(v) => updateFenster({ zweifluegelig: v })} testid="input-f-zweifluegelig" /></Field>
        <Field label="Bodentiefe Fenster / Balkontüren"><NumberInput value={state.fenster.bodentief} onChange={(v) => updateFenster({ bodentief: v })} testid="input-f-bodentief" /></Field>
        <Field label="Dachfenster"><NumberInput value={state.fenster.dachfenster} onChange={(v) => updateFenster({ dachfenster: v })} testid="input-f-dach" /></Field>
        <div className="sm:col-span-2"><YesNo label="Schwer erreichbare Fenster?" value={state.fenster.schwerErreichbar} onChange={(v) => updateFenster({ schwerErreichbar: v })} testid="yn-schwer" /></div>
      </div>
      {includeSoil && windowCount(state.fenster) > 0 && (
        <Field label="Verschmutzungsgrad der Fenster *">
          <SelectInput value={state.fenster.soilLevel} onChange={(value) => updateFenster({ soilLevel: value })} testid="window-soil">
            <option value="">Bitte wählen</option>
            {Object.entries(LABELS.windowSoilLevels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </SelectInput>
        </Field>
      )}
    </div>
  );
}

function DetailsStep({ state, update, updateFenster }) {
  return (
    <div data-testid="step-6" className="space-y-8">
      {includesCleaning(state.serviceType) && state.objectType !== "fenster" && state.objectType !== "sonstiges" && (
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Verschmutzungsgrad des Objekts</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {Object.entries(LABELS.soilLevels).map(([key, label]) => (
              <Chip key={key} selected={state.soilLevel === key} onClick={() => update({ soilLevel: key })} testid={`soil-${key}`}>
                <span className="font-semibold text-[#0F172A]">{label}</span>
              </Chip>
            ))}
          </div>
        </div>
      )}

      {includesCleaning(state.serviceType) && ["wohnung", "haus"].includes(state.objectType) && (
        <div>
          <h3 className="text-lg font-bold text-[#0F172A] mb-4">Zusatzleistungen</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <YesNo label="Backofen innen" value={state.backofen} onChange={(v) => update({ backofen: v })} testid="yn-backofen" />
            <YesNo label="Kühlschrank innen" value={state.kuehlschrank} onChange={(v) => update({ kuehlschrank: v })} testid="yn-kuehlschrank" />
            <YesNo label="Küchenschränke innen" value={state.kuechenschraenke} onChange={(v) => update({ kuechenschraenke: v })} testid="yn-kuechenschraenke" />
            <YesNo label="Tierhaare (+10 %)" value={state.tierhaare} onChange={(v) => update({ tierhaare: v })} testid="yn-tierhaare" />
            <YesNo label="Raucherobjekt (+10 %)" value={state.raucher} onChange={(v) => update({ raucher: v })} testid="yn-raucher" />
          </div>
        </div>
      )}

      {includesCleaning(state.serviceType) && (
        <div>
          <h3 className="text-lg font-bold text-[#0F172A] mb-4">{state.objectType === "fenster" ? "Fensterangaben" : "Fensterreinigung (optional)"}</h3>
          <WindowFields state={state} updateFenster={updateFenster} includeSoil />
        </div>
      )}

      <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-xl p-4 flex gap-3">
        <Info className="w-5 h-5 text-[#0066CC] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">Müllentsorgung, Sonderverschmutzungen und größere Untergrundschäden werden nach Fotos oder Besichtigung angeboten.</p>
      </div>
    </div>
  );
}

function ResultSection({ result, state, isBusiness, onEdit }) {
  const [showForm, setShowForm] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const mainPrice = result.price ?? result.netto;

  useEffect(() => {
    if (!requestSubmitted) return;
    const timer = window.setTimeout(() => {
      document.querySelector('[data-testid="quote-success-confirmation"]')?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [requestSubmitted]);

  return (
    <div data-testid="step-7">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-[#EFF6FF] text-[#0066CC] px-4 py-2 rounded-full text-sm font-semibold mb-3">
          <Calculator className="w-4 h-4" /> Unverbindliche Preiskalkulation
        </div>
        <p className="text-[#64748B] text-sm max-w-2xl mx-auto">Die Berechnung dient als erste Preisorientierung. Der endgültige Angebotspreis kann nach Prüfung der Angaben, Fotos oder einer Besichtigung abweichen.</p>
      </div>

      {result.needsIndividualQuote && (
        <div className="p-6 bg-amber-50 border-2 border-amber-200 rounded-2xl mb-6" data-testid="individual-quote-box">
          <div className="flex gap-3">
            <Sparkles className="w-7 h-7 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-lg font-semibold text-[#0F172A]">Persönliche Prüfung erforderlich</p>
              <p className="text-sm text-[#64748B] mt-1">{result.message || "Für einzelne Positionen benötigen wir Fotos oder eine Besichtigung."}</p>
              {result.hasPrice && <p className="text-sm font-semibold text-amber-800 mt-2">Der angezeigte Betrag ist ein Teil- beziehungsweise Richtpreis und wird vor Auftragserteilung bestätigt.</p>}
            </div>
          </div>
        </div>
      )}

      {result.hasPrice ? (
        <>
          <div className="bg-gradient-to-br from-[#0066CC] to-[#0052A3] text-white rounded-2xl p-6 md:p-8 mb-6" data-testid="main-price-box">
            <div className="text-sm uppercase tracking-widest opacity-80 mb-1">{result.monthly ? "Ihr Richtpreis pro Einsatz" : "Ihr unverbindlicher Richtpreis"}</div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="text-4xl md:text-5xl font-bold" data-testid="main-price-value">{fmtEUR(mainPrice)}</span>
            </div>
            {result.monthly && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="text-sm opacity-80">Geschätzte Monatssumme ({result.monthly.visitsPerMonth} Einsätze/Monat)</div>
                <div className="text-2xl font-bold mt-1" data-testid="monthly-price">
                  {fmtEUR(result.monthly.price ?? result.monthly.netto)}
                </div>
              </div>
            )}
          </div>

          <div className="bg-slate-50 rounded-2xl p-6 mb-6" data-testid="breakdown-box">
            <h3 className="font-bold text-[#0F172A] mb-3">Preisaufteilung</h3>
            <div className="space-y-2">
              {result.breakdown.map((line, index) => (
                <div key={`${line.label}-${index}`} className="flex justify-between gap-4 text-sm">
                  <span className="text-[#334155]">{line.label}</span>
                  <span className="font-medium text-[#0F172A] whitespace-nowrap">{fmtEUR(line.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between text-base font-bold pt-3 border-t border-slate-300 mt-3"><span>Gesamt</span><span>{fmtEUR(result.price ?? result.netto)}</span></div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center p-8 bg-slate-50 border border-slate-200 rounded-2xl mb-6">
          <p className="text-lg font-semibold text-[#0F172A]">Preis nach Prüfung der Angaben</p>
          <p className="text-sm text-[#64748B] mt-2">Sie können die Anfrage jetzt absenden und Fotos ergänzen.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 text-sm mb-6">
        <SummaryRow label="Leistung" value={LABELS.serviceTypes[state.serviceType]} />
        <SummaryRow label="Objektart" value={LABELS.objectTypes[state.objectType]} />
        {["wohnung", "haus"].includes(state.objectType) && state.housingCleaningType ? <SummaryRow label="Reinigungsart" value={LABELS.housingCleaningTypes[state.housingCleaningType]} /> : null}
        <SummaryRow label="Häufigkeit" value={LABELS.frequencies[state.frequency] || "Einmalig"} />
        {state.sqm ? <SummaryRow label="Fläche" value={`${state.sqm} m²`} /> : null}
        {windowCount(state.fenster) > 0 ? <SummaryRow label="Fenster gesamt" value={String(windowCount(state.fenster))} /> : null}
        {state.balconyPresent ? <SummaryRow label="Balkone / Terrassen" value={String(state.balconyCount)} /> : null}
      </div>

      {!showForm && !requestSubmitted && (
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={onEdit} variant="outline" className="rounded-full px-6" data-testid="btn-edit"><ArrowLeft className="w-4 h-4 mr-2" /> Angaben ändern</Button>
          <Button onClick={() => setShowForm(true)} className="bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full px-8 py-6 text-base shadow-lg shadow-[#0066CC]/30" data-testid="btn-request-quote">
            Angebot anfordern <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {showForm && !requestSubmitted && (
        <div className="mt-10">
          <div className="mb-4">
            <Button onClick={() => setShowForm(false)} variant="outline" className="rounded-full" data-testid="btn-back-to-result">
              <ArrowLeft className="w-4 h-4 mr-2" /> Zurück zur Preisübersicht
            </Button>
          </div>
          <QuoteRequestForm
            state={state}
            result={result}
            isBusiness={isBusiness}
            onSuccess={() => setRequestSubmitted(true)}
          />
        </div>
      )}

      {requestSubmitted && (
        <div
          className="mt-10 text-center bg-[#F0FDF4] border-2 border-[#86EFAC] rounded-2xl p-8 md:p-10"
          data-testid="quote-success-confirmation"
          role="status"
          aria-live="polite"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#DCFCE7] flex items-center justify-center">
            <Check className="w-9 h-9 text-[#10B981]" />
          </div>
          <h3 className="text-2xl font-bold text-[#0F172A] mb-3">Vielen Dank für Ihre Anfrage!</h3>
          <p className="text-[#334155] max-w-2xl mx-auto">
            Ihre Anfrage wurde erfolgreich übermittelt. Wir prüfen Ihre Angaben und melden uns schnellstmöglich mit einem persönlichen Angebot bei Ihnen.
          </p>
          <p className="text-sm text-[#64748B] mt-3">Sie müssen nichts weiter tun.</p>
        </div>
      )}
    </div>
  );
}

const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between gap-3 bg-white rounded-lg p-3 border border-slate-100"><span className="text-[#64748B]">{label}</span><span className="font-semibold text-[#0F172A] text-right">{value}</span></div>
);

function buildDetailedSummary(state, result) {
  if (!state || !result) return "Direkte Angebotsanfrage ohne vorherige Kalkulation.";
  const lines = [
    `Leistung: ${LABELS.serviceTypes[state.serviceType] || "-"}`,
    `Kundentyp: ${LABELS.customerTypes[state.customerType] || "-"}`,
    `Objektart: ${LABELS.objectTypes[state.objectType] || "-"}`,
    ["wohnung", "haus"].includes(state.objectType) && state.housingCleaningType ? `Reinigungsart: ${LABELS.housingCleaningTypes[state.housingCleaningType] || "-"}` : null,
    state.customObjectDescription ? `Objektbeschreibung: ${state.customObjectDescription}` : null,
    `Häufigkeit: ${LABELS.frequencies[state.frequency] || "Einmalig"}`,
    state.frequency === "individuell" ? `Einsätze pro Monat: ${state.customVisitsPerMonth}` : null,
    state.customFrequencyText ? `Turnuswunsch: ${state.customFrequencyText}` : null,
    state.sqm ? `Fläche Reinigung: ${state.sqm} m²` : null,
    state.paintingSqm ? `Fläche Malerarbeiten: ${state.paintingSqm} m² Bodenfläche` : null,
    state.objectType === "treppenhaus" ? `Stockwerke: ${state.stockwerke}, Eingänge: ${state.eingaenge}` : null,
    state.balconyPresent ? `Balkone: ${state.balconies.map((b, i) => `${i + 1}. ${LABELS.balconySizes[b.size] || "-"}, ${LABELS.balconySoilLevels[b.soilLevel] || "-"}, ${LABELS.weeds[b.weeds] || "-"}${b.specialCase ? ", Sonderfall" : ""}`).join(" | ")}` : null,
    windowCount(state.fenster) > 0 ? `Fenster: Standard ${state.fenster.standard}, klein ${state.fenster.klein}, zweiflügelig ${state.fenster.zweifluegelig}, bodentief ${state.fenster.bodentief}, Dachfenster ${state.fenster.dachfenster}; Verschmutzung ${LABELS.windowSoilLevels[state.fenster.soilLevel] || "-"}; schwer erreichbar ${state.fenster.schwerErreichbar ? "Ja" : "Nein"}` : null,
    includesPainting(state.serviceType) ? `Malerarbeiten: ${LABELS.paintingScopes[state.paintingScope] || "-"}; aktuelle Wandfarbe ${LABELS.paintingColors[state.paintingCurrentColor] || "-"}; Wandzustand ${LABELS.paintingConditions[state.paintingCondition] || "-"}; neue Farbe ${state.paintingDesiredColor || "-"}; hohe Räume ${state.paintingHighCeiling ? "Ja" : "Nein"}; viele Möbel ${state.paintingFurniture ? "Ja" : "Nein"}` : null,
    state.individualDescription ? `Individuelle Leistung: ${state.individualDescription}` : null,
    state.studioNotes ? `Studio-Besonderheiten: ${state.studioNotes}` : null,
    state.paintingNotes ? `Maler-Hinweise: ${state.paintingNotes}` : null,
    "",
    "--- Preisaufteilung ---",
    ...(result.breakdown || []).map((line) => `${line.label}: ${fmtEUR(line.amount)}`),
    "",
    result.hasPrice ? `Richtpreis: ${fmtEUR(result.price ?? result.netto)}` : "Preis: nach Prüfung",
    result.monthly ? `Monat: ${fmtEUR(result.monthly.price ?? result.monthly.netto)} bei ${result.monthly.visitsPerMonth} Einsätzen` : null,
    result.needsIndividualQuote ? `Prüfhinweis: ${result.message}` : null,
  ].filter(Boolean);
  return lines.join("\n");
}

function QuoteRequestForm({ state = null, result = null, isBusiness = false, direct = false, onSuccess = null }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const [datenschutz, setDatenschutz] = useState(false);

  const handleFilesChange = (e) => setFiles(Array.from(e.target.files || []).slice(0, 5));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!datenschutz) {
      toast.error("Bitte Datenschutz-Zustimmung erteilen.");
      return;
    }
    setSubmitting(true);
    const form = e.currentTarget;
    const selectedFiles = form.elements.bilder?.files;
    if (selectedFiles && selectedFiles.length > 5) {
      toast.error("Bitte laden Sie höchstens fünf Bilder hoch.");
      setSubmitting(false);
      return;
    }
    const fd = new FormData(form);
    fd.set("form-name", "quote-request");
    fd.set("email_ziel", "selina.pfeiffer@frankfurt-reinigung24.de");
    const customerName = `${fd.get("vorname") || ""} ${fd.get("nachname") || ""}`.trim();
    const subject = `Neue Angebotsanfrage – ${state ? LABELS.serviceTypes[state.serviceType] || "Leistung" : "Direkte Anfrage"} – ${state ? LABELS.objectTypes[state.objectType] || "Objekt" : "Objekt"} – ${customerName || "Kunde"} – ${result?.hasPrice ? fmtEUR(result.price ?? result.netto) : "Preis nach Prüfung"}`;
    fd.set("betreff", subject);
    fd.set("anfrage_art", direct ? "Direkte Anfrage" : "Kalkulationsanfrage");
    fd.set("kalkulation_leistung", state ? LABELS.serviceTypes[state.serviceType] || "-" : "Direkte Anfrage");
    fd.set("kalkulation_objektart", state ? LABELS.objectTypes[state.objectType] || "-" : "-");
    fd.set("kalkulation_haeufigkeit", state ? LABELS.frequencies[state.frequency] || "Einmalig" : "-");
    fd.set("kalkulation_verschmutzung", state ? LABELS.soilLevels[state.soilLevel] || "-" : "-");
    fd.set("kalkulation_preis", result?.hasPrice ? fmtEUR(result.price ?? result.netto) : "individuell");
    fd.set("kalkulation_summary", buildDetailedSummary(state, result));
    fd.set("kunde_typ", state ? LABELS.customerTypes[state.customerType] || "-" : "-");
    fd.set("timestamp", new Date().toLocaleString("de-DE"));
    try {
      const response = await fetch("/", { method: "POST", body: fd });
      if (!response.ok) throw new Error("Submit failed");
      setSubmitted(true);
      if (onSuccess) {
        onSuccess();
      } else {
        window.setTimeout(() => {
          document.querySelector('[data-testid="quote-success-box"]')?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 50);
      }
    } catch (error) {
      toast.error("Es gab einen Fehler. Bitte senden Sie Ihre Anfrage per E-Mail an selina.pfeiffer@frankfurt-reinigung24.de.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center bg-[#F0FDF4] border-2 border-[#86EFAC] rounded-2xl p-8" data-testid="quote-success-box">
        <Check className="w-12 h-12 text-[#10B981] mx-auto mb-3" />
        <h3 className="text-xl font-bold text-[#0F172A] mb-2">Vielen Dank für Ihre Anfrage.</h3>
        <p className="text-[#334155]">Wir prüfen Ihre Angaben und melden uns schnellstmöglich mit einem persönlichen Angebot.</p>
      </div>
    );
  }

  return (
    <form name="quote-request" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" encType="multipart/form-data" onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm" data-testid="quote-form">
      <input type="hidden" name="form-name" value="quote-request" />
      <p className="hidden"><label>Bitte nicht ausfüllen: <input name="bot-field" /></label></p>
      <h3 className="text-xl font-bold text-[#0F172A] mb-6">Ihre Kontaktdaten</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Vorname *"><Input name="vorname" required /></Field>
        <Field label="Nachname *"><Input name="nachname" required /></Field>
        <Field label="E-Mail *"><Input type="email" name="email" required /></Field>
        <Field label="Telefon *"><Input type="tel" name="telefon" required /></Field>
        <Field label="Firmenname (optional)"><Input name="firma" /></Field>
        <Field label="Straße & Hausnummer *"><Input name="strasse" required /></Field>
        <Field label="Postleitzahl *"><Input name="plz" required /></Field>
        <Field label="Ort *"><Input name="ort" required /></Field>
        <div className="sm:col-span-2"><Field label="Gewünschter Termin oder Zeitraum *"><Input name="wunschtermin" required placeholder="z. B. ab nächster Woche, KW 32" /></Field></div>
        <div className="sm:col-span-2"><Field label="Nachricht *" hint="Besonderheiten, Fragen, Sonderreinigung, Malerarbeiten, Müllentsorgung etc."><Textarea name="nachricht" required rows={5} /></Field></div>
        <div className="sm:col-span-2">
          <Field label="Fotos hochladen (optional, max. 5)">
            <input type="file" name="bilder" accept="image/*" multiple onChange={handleFilesChange} className="block w-full text-sm text-[#64748B] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:font-medium file:bg-[#EFF6FF] file:text-[#0066CC] hover:file:bg-[#DBEAFE]" />
            {files.length > 0 && <p className="text-xs text-[#64748B] mt-1">{files.length} Datei(en) ausgewählt</p>}
          </Field>
        </div>
      </div>
      <div className="mt-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={datenschutz} onChange={(e) => setDatenschutz(e.target.checked)} className="mt-1" />
          <span className="text-sm text-[#334155]">Ich habe die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#0066CC] underline">Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Daten zu.*</span>
        </label>
      </div>
      <Button type="submit" disabled={submitting} className="w-full mt-6 bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-full py-6 text-base font-medium">
        {submitting ? "Wird gesendet …" : "Anfrage absenden"}
      </Button>
    </form>
  );
}
